"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pipeline = void 0;
const zod_1 = require("zod");
const __1 = require("..");
const _utils = require("./utils");
var pipeline;
(function (pipeline) {
    pipeline.utils = _utils;
    const filterOperation = zod_1.z.object({
        operation: zod_1.z.literal('filter'),
        parameters: __1.common.compositeFilterCondition,
    });
    const relationAggregateOperation = zod_1.z.object({
        operation: zod_1.z.literal('relationAggregate'),
        parameters: zod_1.z
            .object({
            pipelineId: zod_1.z.string().optional(),
            relation: zod_1.z.object({ modelId: zod_1.z.string().optional(), key: zod_1.z.string() }),
            slice: __1.common.slice.optional(),
            aggregations: __1.common.aggregation.array(),
            filters: zod_1.z.object({ parameters: __1.common.filterCondition }).array().optional(),
        })
            .or(zod_1.z.object({
            pipelineId: zod_1.z.string(),
            joinStrategy: zod_1.z.object({
                joinKeyOnBase: zod_1.z.string(),
                joinKeyOnRelated: zod_1.z.string(),
            }),
            slice: __1.common.slice.optional(),
            aggregations: __1.common.aggregation.array(),
            filters: zod_1.z.object({ parameters: __1.common.filterCondition }).array().optional(),
        })),
    });
    const deriveFieldOperation = zod_1.z.object({
        operation: zod_1.z.literal('deriveField'),
        parameters: zod_1.z.object({
            fieldName: zod_1.z.string(),
            value: __1.common.expressionValue,
            key: zod_1.z.string(),
        }),
    });
    const joinPipelineOperation = zod_1.z.object({
        operation: zod_1.z.literal('joinPipeline'),
        parameters: zod_1.z.object({
            pipeline: zod_1.z.lazy(() => pipeline.pipelineWithInstances),
            joinStrategy: zod_1.z.object({
                joinKeyOnBase: zod_1.z.string(),
                joinKeyOnRelated: zod_1.z.string(),
            }),
        }),
    });
    const groupAggregateOperation = zod_1.z.object({
        operation: zod_1.z.literal('groupAggregate'),
        parameters: zod_1.z.object({
            groups: __1.common.grouping.array(),
            slice: __1.common.slice.optional(),
            aggregations: __1.common.aggregation.array(),
        }),
    });
    const switchToRelationOperation = zod_1.z.object({
        operation: zod_1.z.literal('switchToRelation'),
        parameters: zod_1.z.object({
            relation: zod_1.z.object({ modelId: zod_1.z.string().optional(), key: zod_1.z.string() }),
        }),
    });
    const addRelatedColumnOperation = zod_1.z.object({
        operation: zod_1.z.literal('addRelatedColumn'),
        parameters: zod_1.z.object({
            relation: zod_1.z.object({ modelId: zod_1.z.string().optional(), key: zod_1.z.string() }),
            pipelineId: zod_1.z.string().optional(),
            columns: zod_1.z.object({ key: zod_1.z.string(), property: __1.common.property }).array(),
        }),
    });
    const timeSeriesOperation = zod_1.z.object({
        operation: zod_1.z.literal('timeSeries'),
        parameters: zod_1.z.object({
            aggregationType: __1.common.aggregationType,
            aggregationTarget: zod_1.z.string().optional(),
            timeField: zod_1.z.string(),
            timeFieldType: zod_1.z.literal('t_date').or(zod_1.z.literal('t_timestamptz')),
            timeInterval: __1.common.timeInterval,
        }),
    });
    const timeSeriesWithDefaultIntervalOperation = zod_1.z.object({
        operation: zod_1.z.literal('timeSeriesWithDefaultInterval'),
        parameters: zod_1.z.object({
            aggregationType: __1.common.aggregationType,
            aggregationTarget: zod_1.z.string().optional(),
            timeField: zod_1.z.string(),
            timeFieldType: zod_1.z.literal('t_date').or(zod_1.z.literal('t_timestamptz')),
        }),
    });
    const histogramOperation = zod_1.z.object({
        operation: zod_1.z.literal('histogram'),
        parameters: zod_1.z.object({
            key: zod_1.z.string(),
            numBuckets: zod_1.z.number().optional(),
        }),
    });
    const funnelOperation = zod_1.z.object({
        operation: zod_1.z.literal('funnel'),
        parameters: zod_1.z.object({
            steps: zod_1.z.array(zod_1.z.object({
                pipeline: zod_1.z.lazy(() => pipeline.pipelineWithInstances),
                sortKey: zod_1.z.string(),
                fields: zod_1.z.array(zod_1.z.string()).nonempty(),
            })),
            groups: zod_1.z.array(zod_1.z.object({
                key: zod_1.z.string(),
                precision: __1.common.timeInterval.optional(),
            })),
            property: __1.common.property,
        }),
    });
    const cohortOperation = zod_1.z.object({
        operation: zod_1.z.literal('cohort'),
        parameters: zod_1.z.object({
            pipeline: zod_1.z.lazy(() => pipeline.pipelineWithInstances),
            cohortId: zod_1.z.string(),
            cohortTimeKey: zod_1.z.string(),
            cohortTimeInterval: __1.common.cohortTimeInterval.optional(),
            eventTimeKey: zod_1.z.string(),
            eventTimeInterval: __1.common.cohortTimeInterval.optional(),
        }),
    });
    const sqlOperation = zod_1.z.object({
        operation: zod_1.z.literal('sql'),
        parameters: zod_1.z.object({
            sql: zod_1.z.string(),
            fields: zod_1.z
                .object({
                key: zod_1.z.string(),
                type: zod_1.z.string().optional(),
            })
                .array()
                .optional(),
        }),
    });
    const pythonOperation = zod_1.z.object({
        operation: zod_1.z.literal('python'),
        parameters: zod_1.z.object({
            code: zod_1.z.string(),
        }),
    });
    pipeline.operation = zod_1.z
        .union([
        addRelatedColumnOperation,
        relationAggregateOperation,
        deriveFieldOperation,
        groupAggregateOperation,
        switchToRelationOperation,
        filterOperation,
        joinPipelineOperation,
        timeSeriesOperation,
        timeSeriesWithDefaultIntervalOperation,
        histogramOperation,
        funnelOperation,
        cohortOperation,
        sqlOperation,
        pythonOperation,
    ])
        .and(zod_1.z.object({ disabled: zod_1.z.boolean().optional() }));
    pipeline.basePipeline = zod_1.z.object({
        baseModelId: zod_1.z.string(),
        operations: zod_1.z.array(pipeline.operation),
        pipelineId: zod_1.z.string().optional(),
    });
    pipeline.pipelineWithInstances = pipeline.basePipeline.or(zod_1.z.object({
        parentId: zod_1.z.string(),
        operations: zod_1.z.array(pipeline.operation),
        pipelineId: zod_1.z.string().optional(),
    }));
})(pipeline || (exports.pipeline = pipeline = {}));
//# sourceMappingURL=index.js.map